import { Injectable } from '@angular/core';
import { LogRocketService } from '../logrocket-service/logrocket.service';

@Injectable({ providedIn: 'root' })
export class SignalRLogger {
  constructor(private logRocketService: LogRocketService) {}

  private formatTime(): string {
    return new Date().toISOString();
  }

  connected(groupId: string, connectionId?: string | null, transport?: string, trigger?: string): void {
    const msg = `[SignalR ${this.formatTime()}] CONNECTED`;
    const context: Record<string, any> = {
      groupId,
      connectionId: connectionId ?? 'unknown',
      transport: transport ?? 'unknown',
      trigger: trigger ?? 'unknown'
    };
    console.log(`%c${msg}`, 'color: green; font-weight: bold', context);
    this.logRocketService.track('SignalR.Connected', context);

    // Anything but WebSockets is a degraded connection worth surfacing.
    if (transport && transport !== 'WebSockets' && transport !== 'unknown') {
      const warnMsg = `[SignalR ${this.formatTime()}] WARNING: Using ${transport} transport instead of WebSockets`;
      console.warn(`%c${warnMsg}`, 'color: red; font-weight: bold');
      this.logRocketService.captureMessage(warnMsg, {
        tags: { feature: 'signalr', severity: 'warning' },
        extra: context
      });
    }
  }

  disconnected(reason: string, error?: any): void {
    const msg = `[SignalR ${this.formatTime()}] DISCONNECTED — reason: ${reason}`;
    const context: Record<string, any> = { reason, error: error?.message ?? error ?? '' };
    console.warn(`%c${msg}`, 'color: red; font-weight: bold', context);
    this.logRocketService.captureMessage(msg, {
      tags: { feature: 'signalr', severity: 'warning' },
      extra: context
    });
    this.logRocketService.track('SignalR.Disconnected', context);
  }

  reconnecting(attempt: number, delay: number, connectionId?: string | null): void {
    const msg = `[SignalR ${this.formatTime()}] RECONNECTING`;
    const context = { attempt, delay, connectionId: connectionId ?? 'unknown' };
    console.log(`%c${msg}`, 'color: orange; font-weight: bold', context);
    this.logRocketService.track('SignalR.Reconnecting', context);
  }

  reconnected(groupId: string, connectionId?: string | null, previousConnectionId?: string | null, trigger?: string): void {
    const msg = `[SignalR ${this.formatTime()}] RECONNECTED`;
    const context: Record<string, any> = {
      groupId,
      connectionId: connectionId ?? 'unknown',
      trigger: trigger ?? 'unknown'
    };
    if (previousConnectionId) {
      context['previousConnectionId'] = previousConnectionId;
    }
    console.log(`%c${msg}`, 'color: green; font-weight: bold', context);
    this.logRocketService.track('SignalR.Reconnected', context);
  }

  groupJoined(groupId: string, connectionId?: string | null): void {
    const msg = `[SignalR ${this.formatTime()}] GROUP JOINED`;
    const context = { groupId, connectionId: connectionId ?? 'unknown' };
    console.log(msg, context);
    this.logRocketService.track('SignalR.GroupJoined', context);
  }

  groupJoinFailed(groupId: string, error: any): void {
    const msg = `[SignalR ${this.formatTime()}] GROUP JOIN FAILED`;
    const context = { groupId, error: error?.message ?? String(error) };
    console.error(msg, context);
    this.logRocketService.captureMessage(msg, {
      tags: { feature: 'signalr', severity: 'error' },
      extra: context
    });
  }

  eventReceived(event: string, entityCount?: number): void {
    console.debug(`[SignalR ${this.formatTime()}] EVENT: ${event}`,
      entityCount != null ? { entityCount } : '');
  }

  retryPolicyDelay(attempt: number, delay: number): void {
    const msg = `[SignalR ${this.formatTime()}] RETRY POLICY — attempt: ${attempt}, delay: ${Math.round(delay)}ms`;
    console.log(msg);
    this.logRocketService.track('SignalR.RetryPolicy', { attempt, delay: Math.round(delay) });
  }

  retryPolicyExhausted(attempt: number): void {
    const msg = `[SignalR ${this.formatTime()}] LAYER 1 EXHAUSTED — automatic reconnect gave up after ${attempt} attempts, handing off to Layer 2`;
    console.warn(`%c${msg}`, 'color: orange; font-weight: bold');
    this.logRocketService.track('SignalR.Layer1Exhausted', { attempt });
  }

  layer1Started(error?: any, connectionId?: string | null): void {
    const msg = `[SignalR ${this.formatTime()}] LAYER 1 STARTED — automatic reconnect initiated`;
    const context: Record<string, any> = { connectionId: connectionId ?? 'unknown' };
    if (error) context['error'] = error?.message ?? String(error);
    console.log(`%c${msg}`, 'color: orange; font-weight: bold', context);
    this.logRocketService.track('SignalR.Layer1Started', context);
  }

  networkOnlineRecovery(): void {
    const msg = `[SignalR ${this.formatTime()}] LAYER 3 — network online event, triggering reconnect`;
    console.log(`%c${msg}`, 'color: blue; font-weight: bold');
    this.logRocketService.track('SignalR.NetworkOnlineRecovery', {});
  }

  visibilityRecovery(dormantDurationMs: number): void {
    const msg = `[SignalR ${this.formatTime()}] LAYER 4 — tab became visible after ${Math.round(dormantDurationMs / 1000)}s, triggering reconnect`;
    console.log(`%c${msg}`, 'color: blue; font-weight: bold', { dormantDurationMs });
    this.logRocketService.track('SignalR.VisibilityRecovery', { dormantDurationMs });
  }

  freshnessCheckPassed(): void {
    // Console only: this fires every 2 minutes, too noisy for LogRocket.
    console.debug(`[SignalR ${this.formatTime()}] LAYER 5 — freshness check passed`);
  }

  freshnessCheckFailed(error?: any): void {
    const msg = `[SignalR ${this.formatTime()}] LAYER 5 — freshness check failed, connection stale`;
    const context = { error: error?.message ?? String(error ?? '') };
    console.warn(`%c${msg}`, 'color: red; font-weight: bold', context);
    this.logRocketService.captureMessage(msg, {
      tags: { feature: 'signalr', severity: 'warning' },
      extra: context
    });
  }

  stateChange(from: string, to: string): void {
    console.log(`[SignalR ${this.formatTime()}] STATE: ${from} → ${to}`);
  }

  error(operation: string, context: Record<string, any>): void {
    const msg = `[SignalR ${this.formatTime()}] ERROR: ${operation}`;
    console.error(msg, context);
    this.logRocketService.captureMessage(msg, {
      tags: { feature: 'signalr', severity: 'error' },
      extra: context
    });
  }
}
